import axios from 'axios';
import { CheckoutRequest } from '../../models/CheckoutRequest';
import { CHECKOUT_LOADING } from './types';
import { store } from '../store';
import { redirect } from '../../utils/redirect';
import { triggerModal } from './appState';
import { translate } from '../../localization';
import {
  APPLE_PAY_LABEL,
  APPLE_PAY_MERCHANT_CAPABILITIES,
  APPLE_PAY_SUPPORTED_METHODS,
  APPLE_PAY_SUPPORTED_NETWORKS,
  APPLE_PAYMENT_SESSION_URL,
  DEFAULT_COUNTRY_SHORT_CODE,
  DEFAULT_CURRENCY,
} from '../../components/form/paymentTypes';

export const paymentLoading = (isLoading) => ({
  type: CHECKOUT_LOADING,
  payload: { isLoading },
});

export const requestCardToken = (cardData) => {
  const meta = store.getState().metadata.object;
  store.dispatch(paymentLoading(true));
  return axios
    .post(meta.requestTokenUrl, CheckoutRequest.getTokenRequest(cardData), {
      headers: { Authorization: meta.publishableKey },
    })
    .then((res) => res.data.token)
    .catch(({ response }) => {
      store.dispatch(paymentLoading(false));
      const errorCodes = response?.data?.error_codes;
      const translatedErrorCodes = errorCodes?.map((error) => translate(error));
      store.dispatch(triggerModal(true, translatedErrorCodes?.join('\r\n')));
      throw response;
    });
};

export const requestPayment = (token) => {
  const meta = store.getState().metadata.object;
  return axios
    .post(meta.requestPaymentUrl, CheckoutRequest.getPaymentRequest(token), {
      headers: { Authorization: meta.publishableKey },
    })
    .then((response) => {
      redirect(response.data.redirect_url);
    })
    .catch((error) => {
      store.dispatch(paymentLoading(false));
      store.dispatch(triggerModal(true, error?.response?.data?.message));
    });
};

export const storeCard = (token) => {
  const meta = store.getState().metadata.object;
  return axios.post(
    `${process.env.REACT_APP_BACKEND_URL}/payments/store-card`,
    {
      card_token: token,
      subscription_id: meta.subscriptionId,
    },
  );
};

export const validateAppleMerchant = (validation_url) => {
  const language = store.getState().appState.language;
  const headers = { headers: { 'Accept-Language': language } };
  const data = { validation_url };
  return axios
    .post(
      `${process.env.REACT_APP_BACKEND_URL}/payments/validate-apple-merchant`,
      data,
      headers,
    )
    .then((paymentResponse) => {
      return paymentResponse.data;
    });
};

export const payWithApplePay = (subscription_id, apple_token) => {
  const language = store.getState().appState.language;
  const headers = { headers: { 'Accept-Language': language } };
  const data = { subscription_id, apple_token };
  return axios.post(
    `${process.env.REACT_APP_BACKEND_URL}/payments/pay-with-apple-checkout-web`,
    data,
    headers,
  );
};

export const paymentRequestHandel = (subscriptionId, meta) => {
  // Define PaymentMethodData
  const paymentMethodData = [
    {
      supportedMethods: APPLE_PAY_SUPPORTED_METHODS,
      data: {
        version: 3,
        merchantIdentifier: meta?.merchantIdentifier,
        merchantCapabilities: APPLE_PAY_MERCHANT_CAPABILITIES,
        supportedNetworks: APPLE_PAY_SUPPORTED_NETWORKS,
        countryCode: DEFAULT_COUNTRY_SHORT_CODE,
      },
    },
  ];

  // Define PaymentDetails
  const paymentDetails = {
    displayItems: [
      {
        label: APPLE_PAY_LABEL,
        amount: {
          value: meta?.amount.toFixed(2).toString(),
          currency: DEFAULT_CURRENCY,
        },
      },
    ],
    total: {
      label: APPLE_PAY_LABEL,
      amount: {
        value: meta?.amount.toFixed(2).toString(),
        currency: DEFAULT_CURRENCY,
      },
    },
  };

  // Define PaymentOptions
  const paymentOptions = {};

  // Create PaymentRequest
  const applePaymentRequest = new window.PaymentRequest(
    paymentMethodData,
    paymentDetails,
    paymentOptions,
  );

  applePaymentRequest.onmerchantvalidation = async (event) => {
    // Call your own server to request a new merchant session.
    const merchantSessionPromise = await validateAppleMerchant(
      APPLE_PAYMENT_SESSION_URL,
    );
    event.complete(merchantSessionPromise);
  };

  applePaymentRequest.canMakePayment().then((isCanMakePayment) => {
    if (isCanMakePayment) {
      applePaymentRequest.show().then((paymentResponse) => {
        const { paymentData } = paymentResponse.details.token;
        payWithApplePay(subscriptionId, JSON.stringify(paymentData))
          .then((response) => {
            paymentResponse.complete('success');
            redirect(response.data.redirect_url);
          })
          .catch(() => {
            paymentResponse.complete('fail');
          });
      });
    }
  });
};

export const applePaySessionHandel = (subscriptionId, meta) => {
  // Define ApplePayPaymentRequest
  const applePayPaymentRequest = {
    countryCode: DEFAULT_COUNTRY_SHORT_CODE,
    currencyCode: DEFAULT_CURRENCY,
    merchantCapabilities: APPLE_PAY_MERCHANT_CAPABILITIES,
    supportedNetworks: APPLE_PAY_SUPPORTED_NETWORKS,
    total: {
      label: APPLE_PAY_LABEL,
      amount: meta?.amount.toFixed(2).toString(),
    },
  };

  // Create ApplePaySession
  const applePaySession = new window.ApplePaySession(3, applePayPaymentRequest);

  applePaySession.onvalidatemerchant = async (event) => {
    // Call your own server to request a new merchant session.
    const merchantSession = await validateAppleMerchant(event.validationURL);
    applePaySession.completeMerchantValidation(merchantSession);
  };

  applePaySession.onpaymentauthorized = (event) => {
    // Define ApplePayPaymentAuthorizationResult
    const { paymentData } = event.payment.token;
    payWithApplePay(subscriptionId, JSON.stringify(paymentData))
      .then((response) => {
        applePaySession.completePayment({
          status: window.ApplePaySession.STATUS_SUCCESS,
        });
        redirect(response.data.redirect_url);
      })
      .catch(() => {
        applePaySession.completePayment({
          status: window.ApplePaySession.STATUS_FAILURE,
        });
      });
  };

  applePaySession.begin();
};
